// Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import "./../styles/Dashboard.css";

import SubjectArrow from "../Logos To be used/subject arrow.svg";

// ---- constants ----
const DEFAULT_FACULTY_ID = "1c4e71de-9018-44d3-929e-042704d86516";
const TRACKED_SUBJECT_ID = "b32e5740-875d-4055-9d61-8253d0714757";
const CLOSED_SUBJECT_IDS = new Set([
  "385999b9-532a-4526-8ba4-40c86f47a0f2",
  "94ce3248-7a00-40be-b5f6-8c4f831ad526",
  "d2c5857d-4d6c-4e1d-aaa5-a6a0875cac40",
]);

// ---- GraphQL ----
const STUDENT_SUBJECT_PROGRESS_QUERY = gql`
  query StudentSubjectProgress($filter: StudentSubjectProgressFilterInput!) {
    studentSubjectProgress(filter: $filter) {
      userId
      subjectId
      progress
      updatedAt
    }
  }
`;

const SUBJECT_TITLE_QUERY = gql`
  query Subject($id: ID!) {
    subject(id: $id) {
      id
      title
      slug
    }
  }
`;

const FACULTY_CATEGORIES_QUERY = gql`
  query FacultyCategoriesByFaculty($facultyId: ID!) {
    facultyCategoriesByFaculty(facultyId: $facultyId) {
      id
      facultyId
      name
      slug
      position
    }
  }
`;

const SUBJECTS_BY_CATEGORY_QUERY = gql`
  query SubjectsByFacultyCategory($facultyCategoryId: ID!) {
    subjectsByFacultyCategory(facultyCategoryId: $facultyCategoryId) {
      position
      subject {
        id
        title
        slug
        description
        imageUrl
        isPublic
      }
      facultyCategory {
        id
      }
    }
  }
`;

const sortByNumber = (a, b, key) => {
  const av = typeof a?.[key] === "number" ? a[key] : 0;
  const bv = typeof b?.[key] === "number" ? b[key] : 0;
  return av - bv;
};

const getSubjectDashboardPath = (subject) => {
  if (CLOSED_SUBJECT_IDS.has(subject?.id || "")) {
    return `/subject-closed/${subject.id}`;
  }
  return subject?.slug ? `/subject/${subject.slug}` : "/subject-example";
};

const Dashboard = () => {
  const userId = localStorage.getItem("userID");

  // Progress
  const { data: progressData } = useQuery(STUDENT_SUBJECT_PROGRESS_QUERY, {
    variables: { filter: { userId, subjectId: TRACKED_SUBJECT_ID } },
    skip: !userId,
    fetchPolicy: "network-only",
  });

  const progressPercent = progressData?.studentSubjectProgress?.progress || 0;
  const progressValue = Math.max(0, Math.min(100, Number(progressPercent) || 0));

  const { data: trackedSubjectData } = useQuery(SUBJECT_TITLE_QUERY, {
    variables: { id: TRACKED_SUBJECT_ID },
    fetchPolicy: "cache-first",
  });

  const trackedSubject = trackedSubjectData?.subject;
  const trackedSubjectTitle = trackedSubject?.title || "Weiterlernen";
  const continuePath = trackedSubject
    ? getSubjectDashboardPath(trackedSubject)
    : "/subject-example";

  // Faculty categories
  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    FACULTY_CATEGORIES_QUERY,
    {
      variables: { facultyId: DEFAULT_FACULTY_ID },
      fetchPolicy: "network-only",
    }
  );

  const sortedCategories = useMemo(() => {
    const list = categoriesData?.facultyCategoriesByFaculty || [];
    return [...list].sort((a, b) => sortByNumber(a, b, "position"));
  }, [categoriesData]);

  // Category row component
  const CategoryRow = ({ category }) => {
    const scrollerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const { data, loading } = useQuery(SUBJECTS_BY_CATEGORY_QUERY, {
      variables: { facultyCategoryId: category.id },
      fetchPolicy: "network-only",
    });

    const publicSubjects = useMemo(() => {
      const rel = data?.subjectsByFacultyCategory || [];
      const ordered = [...rel].sort((a, b) => sortByNumber(a, b, "position"));
      return ordered
        .filter((x) => x?.subject?.isPublic === true)
        .map((x) => x.subject);
    }, [data]);

    const shouldRender = loading || publicSubjects.length > 0;

    const updateScrollButtons = () => {
      const el = scrollerRef.current;
      if (!el) return;
      const left = el.scrollLeft;
      const maxLeft = el.scrollWidth - el.clientWidth;

      setCanScrollLeft(left > 2);
      setCanScrollRight(maxLeft - left > 2);
    };

    useEffect(() => {
      if (!shouldRender) return;

      updateScrollButtons();
      const el = scrollerRef.current;
      if (!el) return;

      const onScroll = () => updateScrollButtons();
      el.addEventListener("scroll", onScroll);
      window.addEventListener("resize", updateScrollButtons);

      return () => {
        el.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", updateScrollButtons);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRender, publicSubjects.length]);

    const scrollByCards = (dir) => {
      const el = scrollerRef.current;
      if (!el) return;
      const amount = dir === "left" ? -700 : 700;
      el.scrollBy({ left: amount, behavior: "smooth" });
    };

    if (!shouldRender) return null;

    return (
      <div className="subject-category">
        <div className="category-header">
          <h2 className="category-title">{category.name}</h2>
          {!loading && (
            <span className="category-meta">
              {publicSubjects.length} {publicSubjects.length === 1 ? "Fach" : "Fächer"}
            </span>
          )}
        </div>

        <div className="category-row">
          {canScrollLeft && (
            <button
              className="scroll-btn scroll-btn-left"
              onClick={() => scrollByCards("left")}
              aria-label="Nach links"
              type="button"
            >
              ‹
            </button>
          )}

          <div className="cards-clip">
            <div className="subject-cards-scroller" ref={scrollerRef}>
              <div className="subject-cards">
                {loading &&
                  Array.from({ length: 4 }).map((_, idx) => (
                    <div className="subject-card skeleton" key={`sk-${idx}`} />
                  ))}

                {!loading &&
                  publicSubjects.map((s) => (
                    <Link
                      className="subject-card"
                      key={s.id}
                      to={getSubjectDashboardPath(s)}
                      aria-label={`${s.title} öffnen`}
                    >
                      {s.imageUrl ? (
                        <img
                          src={s.imageUrl}
                          alt={s.title}
                          className="subject-image"
                        />
                      ) : (
                        <div className="subject-image placeholder">
                          <div className="placeholder-line" />
                          <div className="placeholder-line short" />
                          <div className="placeholder-line" />
                        </div>
                      )}

                      <div className="subject-card-body">
                        <div className="subject-title-dashboard">{s.title}</div>

                        {!!s.description && (
                          <div className="subject-mini-description">
                            {s.description}
                          </div>
                        )}
                      </div>

                      <img
                        src={SubjectArrow}
                        alt="Entry"
                        className="subject-entry"
                      />
                    </Link>
                  ))}
              </div>
            </div>
          </div>

          {canScrollRight && (
            <button
              className="scroll-btn scroll-btn-right"
              onClick={() => scrollByCards("right")}
              aria-label="Nach rechts"
              type="button"
            >
              ›
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard dashboard-student">
      <div className="dashboard-inner">
        <section className="dashboard-hero-panel" aria-label="Dashboard Einführung">
          <div className="dashboard-hero-copy">
            <p className="dashboard-eyebrow">Student Dashboard</p>
            <h1 className="dashboard-hero-title">
              Inhalt Geprüft und erstellt von Unilehrenden.
            </h1>
            <p className="dashboard-hero-subtitle">
              Deine Lernumgebung ist bereit. Setze deinen Fortschritt strukturiert
              fort und greife direkt auf deine aktiven Fachbereiche zu.
            </p>
          </div>
        </section>

        {progressValue > 0 && (
          <section className="progress-wrapper" aria-label="Weiterlernen">
            <div className="progress-section">
              <div className="progress-headline">
                <p className="progress-eyebrow">Weiterlernen</p>
                <h2 className="progress-title">An deinem letzten Punkt fortsetzen</h2>
              </div>

              <div className="progress-item">
                <div className="progress-main">
                  <span className="progress-subject">{trackedSubjectTitle}</span>
                  <div className="progress-meta">
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${progressValue}%` }}
                      />
                    </div>
                    <span className="progress-percent">{progressValue} %</span>
                  </div>
                </div>

                <Link className="progress-button" to={continuePath}>
                  Weiter
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="lower-section" aria-label="Fachkategorien">
          {categoriesLoading ? (
            <div className="categories-loading">Loading...</div>
          ) : (
            sortedCategories.map((cat) => <CategoryRow key={cat.id} category={cat} />)
          )}

          <div className="footer-spacing" />
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
